import React, { LegacyRef, useEffect, useState } from 'react';
import { Button, InfoWrapper, Modal } from 'app/components';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import { useGetUserApikeyQuery, useUpdateUserProfileSettingsMutation } from 'app/api/facade';
import { UserI } from 'app/utils/userSlice';
import { useAlertHook } from 'app/hooks';
import { useAppSelector } from 'app/store/store';
import moment from 'moment';
import { UserProfileSettingsPatchParams } from 'ais-facade-api/api';

import styles from './UserSettingsModal.module.scss';

export enum TGSubscriptionEnum {
  NotSubscribed = 'NOT_SUBSCRIBED',
  Subscribed = 'SUBSCRIBED',
}
interface Props {
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  user: UserI;
  isShown: boolean;
}

export const UserSettingsModal = ({ innerRef, onClose, user, isShown }: Props) => {
  const { addAlert } = useAlertHook();
  const { t, i18n } = useTranslation();
  const [updateSettings] = useUpdateUserProfileSettingsMutation();

  const profile = useAppSelector((state) => state.userSlice?.profile);

  const { data: userApikeyData } = useGetUserApikeyQuery();

  const [formValues, setFormValues] = useState({
    isLocalTz: profile?.ui?.isLocalTz,
    timeFormat: profile?.ui?.timeFormat,
    coordinatesFormat: profile?.ui?.coordinatesFormat,
    languageTag: profile?.system?.languageTag,
  });

  const UTCOffset = moment().utcOffset() / 60;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const settingsParams: UserProfileSettingsPatchParams = {
      system: { languageTag: formValues.languageTag },
      ui: {
        isLocalTz: formValues.isLocalTz,
        timeFormat: formValues.timeFormat,
        coordinatesFormat: formValues.coordinatesFormat,
      },
    };
    updateSettings(settingsParams)
      .unwrap()
      .then(async () => {
        if (formValues.languageTag) {
          await i18n.changeLanguage(formValues.languageTag);
        }
        addAlert({
          data: {
            title: 'Success',
            status: 200,
            detail: 'Success',
            messages: [{ description: t('ChangesSaved'), type: 'information' }],
          },
        });
        onClose();
      })
      .catch((error) => {
        addAlert(error);
      });
  };

  const handleCopyApiKey = () => {
    userApikeyData?.result &&
      navigator.clipboard.writeText(userApikeyData.result).then(() => {
        addAlert({
          data: {
            messages: [{ description: t('ClipboardCopySuccess'), type: 'information' }],
          },
        });
      });
  };

  useEffect(() => {
    if (profile?.ui && profile?.system) {
      setFormValues((prev) => {
        return {
          ...prev,
          isLocalTz: profile?.ui?.isLocalTz,
          timeFormat: profile?.ui?.timeFormat,
          coordinatesFormat: profile?.ui?.coordinatesFormat,
          languageTag: profile?.system?.languageTag,
        };
      });
    }
  }, [profile?.ui, profile?.system]);

  return (
    <div className={styles.root}>
      <Modal
        headerTitle={t('AccountOptions')}
        onClose={onClose}
        isShown={isShown}
        className={styles.modal_root}
        componentClass={styles.wrapper}
        component={
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.dialog_modal} ref={innerRef}>
              <div className={styles.container}>
                <div className={styles.head}>
                  <div className={styles.block_title}>{`${t('Account')}:`}</div>
                  <InfoWrapper
                    label={`${t('Login')}:`}
                    value={user?.value.preferred_username ? user?.value.preferred_username : <>&mdash;</>}
                  />
                  <InfoWrapper
                    label={`${t('APIKey')}:`}
                    value={
                      userApikeyData?.result ? (
                        <div title={t('ClipboardCopy')} className={styles.copy} onClick={handleCopyApiKey}>
                          <span>{userApikeyData.result}</span>
                          <Icon name="copy" size="14px" />
                        </div>
                      ) : (
                        <>&mdash;</>
                      )
                    }
                  />
                </div>

                <div className={styles.block}>
                  <div className={styles.block_title}>{`${t('PreferredSettings')}:`}</div>
                  <InfoWrapper
                    label={`${t('Language')}:`}
                    value={
                      <select
                        name="languageTag"
                        value={formValues.languageTag}
                        className={styles.date_select}
                        onChange={handleChange}
                      >
                        <option value="ru">{t(`RussianLanguage`)}</option>
                        <option value="en-US">{t(`EnglishLanguage`)}</option>
                      </select>
                    }
                  />
                  <InfoWrapper
                    label={`${t('TimeFormatOption')}:`}
                    value={
                      <select
                        name="timeFormat"
                        value={formValues.timeFormat}
                        className={styles.date_select}
                        onChange={handleChange}
                      >
                        <option value="hh:mm:ss A">12H</option>
                        <option value="HH:mm:ss">24H</option>
                      </select>
                    }
                  />
                  <InfoWrapper
                    label={`${t('TimeZoneOption')}:`}
                    value={
                      <select
                        name="isLocalTz"
                        value={formValues.isLocalTz}
                        className={styles.date_select}
                        onChange={handleChange}
                      >
                        <option value={'true'}>UTC +{UTCOffset}</option>
                        <option value={'false'}>UTC</option>
                      </select>
                    }
                  />
                  <InfoWrapper
                    label={`${t('CoordinatesFormatOption')}:`}
                    value={
                      <select
                        name="coordinatesFormat"
                        value={formValues.coordinatesFormat}
                        className={styles.date_select}
                        onChange={handleChange}
                      >
                        <option value={'DMS'}>DD° MM′ SS″</option>
                        <option value={'DD'}>DD.DDDDD</option>
                        <option value={'DMM'}>DD° MM.MMMM</option>
                      </select>
                    }
                  />
                </div>
              </div>
              <div className={styles.actions}>
                <Button className={styles.action_button} type="submit" label={t('FilterPanelButtonSubmit')} variant="general" />
                <Button
                  className={styles.action_button}
                  type="button"
                  label={t('ButtonLabelDiscard')}
                  variant="general"
                  onClick={onClose}
                />
              </div>
            </div>
          </form>
        }
      />
    </div>
  );
};
